import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LoginInput, MeResponse, UserPublic, LoginResponseWithFlag } from '@underline/shared';
import { api, HTTPError } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';

export const ME_QUERY_KEY = ['me'] as const;
export const ME_STALE_TIME = 5 * 60 * 1000;

/**
 * Busca o usuário logado (/auth/me) e sincroniza o store (Zustand) via getState,
 * para ser reusada tanto no `useMe` quanto no prefetch do BootGate. Retorna null em 401.
 */
export async function fetchMe(): Promise<UserPublic | null> {
  const setUser = useAuthStore.getState().setUser;
  try {
    const data = await api.get('auth/me').json<MeResponse>();
    setUser(data.user);
    return data.user;
  } catch (err) {
    if (err instanceof HTTPError && err.response.status === 401) {
      setUser(null);
      return null;
    }
    throw err;
  }
}

export function useMe(): {
  user: UserPublic | null;
  isLoading: boolean;
  isAuthenticated: boolean;
} {
  const cached = useAuthStore((s) => s.user);

  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: ME_STALE_TIME,
  });

  return {
    user: query.data ?? cached,
    isLoading: query.isLoading,
    isAuthenticated: (query.data ?? cached) !== null,
  };
}

export function useLogin(): ReturnType<typeof useMutation<LoginResponseWithFlag, Error, LoginInput>> {
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: LoginInput) => api.post('auth/login', { json: input }).json<LoginResponseWithFlag>(),
    onSuccess: (data) => {
      setUser(data.user);
      qc.setQueryData(['me'], data.user);
    },
  });
}

export function useLogout(): ReturnType<typeof useMutation<void, Error, void>> {
  const clear = useAuthStore((s) => s.clear);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post('auth/logout');
    },
    onSuccess: () => {
      clear();
      qc.clear();
    },
  });
}
